import { BusinessObjectPart, BusinessObjectPartContext, ModelBase, ObservableCollection, Weaver } from '@skylab-shared/core';
import { DocumentItemModel } from '@skylab-shared/dataModel';
import { unique } from '@skylab-shared/utils';
import { ContextKeys } from '../../common/context-keys';
import { propertyChangedFilter } from '../../common/property-changed/property-changed-filter';
import { CommunityTransactionForSaleQuery } from '../../community-transaction/queries/community-transaction-for-sale.query-base';
import { VatRateForSaleQuery } from '../../vat/queries/vat-rate-for-sale.query-base';
import { WrongVatRateUsedOnDocumentError } from '../violations/wrong-vat-rate-used-on-document-error';


export interface DocumentVatRateForSaleValidationBoPartModelPart extends ModelBase {
    items: ObservableCollection<DocumentItemModel>;
    communityTransactionId: number;
    wstoConsumerCountryId: number;
}

export class DocumentVatRateForSaleValidationBoPart extends BusinessObjectPart<DocumentVatRateForSaleValidationBoPartModelPart> {
    public constructor(
        weaver: Weaver,
    ) {
        super(weaver);
    }

    public override async initializeAsync(context: BusinessObjectPartContext<DocumentVatRateForSaleValidationBoPartModelPart>): Promise<void> {
        await super.initializeAsync(context);
        this.bo.localContext.preload(ContextKeys.vatRatesUsedInWstoDocument(this.bo.model.sun), () => Promise.resolve(this.vatRatesUsedInWstoDocument));
        this.registerCorrectVatRateForDocumentValidationRules();
    }

    private registerCorrectVatRateForDocumentValidationRules(): void {
        this.coreFeature.propertyChanged.listen<DocumentVatRateForSaleValidationBoPartModelPart>(propertyChangedFilter(
            this.modelType,
            ['communityTransactionId', 'wstoConsumerCountryId'],
            () => this.engine.validation.queueValidation([DocumentItemModel, 'vatRateId'])
        ));

        this.engine.validation.addRule(
            [[DocumentItemModel, 'vatRateId']],
            ctx => this.documentItemHasCorrectVatRate(ctx.source),
            WrongVatRateUsedOnDocumentError
        );
    }

    private documentItemHasCorrectVatRate(item: DocumentItemModel): boolean {
        if (!item.vatRateId) return true;

        const vatRates = this.bo.localContext.getPreloaded(VatRateForSaleQuery);
        let vatRatesForCommunityTransaction = vatRates?.filter(v => v.communityTransactions.indexOf(this.bo.model.communityTransactionId) > -1);

        if (this.isWstoOssTransactionSelected) {
            vatRatesForCommunityTransaction = vatRatesForCommunityTransaction?.filter(elem => elem.countryId === this.bo.model.wstoConsumerCountryId);
        }

        return vatRatesForCommunityTransaction?.some(v => v.id === item.vatRateId) ?? false;
    }

    private get vatRatesUsedInWstoDocument(): number[] {
        if (this.bo.model.isPersistent && this.isWstoOssTransactionSelected) {
            return unique(this.bo.model.items.map(elem => elem.vatRateId));
        }
        return [];
    }

    private get isWstoOssTransactionSelected(): boolean {
        const wstoVatRate = this.bo.localContext.firstPreloaded(CommunityTransactionForSaleQuery, { symbol: 'WSTO-OSS' });
        return this.bo.model.communityTransactionId === wstoVatRate?.id;
    }
}
